import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: 'accurate' | 'normal';
  } = {}
) {
  const { decimals = 0, sizeType = 'normal' } = opts;

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === 'accurate' ? accurateSizes[i] ?? 'Bytest' : sizes[i] ?? 'Bytes'
  }`;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function makeCamelCase(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

const emptyStringToUndefined = z.literal('').transform(() => undefined);
export function asOptionalField<T extends z.ZodTypeAny>(schema: T) {
  return schema.optional().or(emptyStringToUndefined);
}

export function imageValidation(purpose: 'dp' | 'logo') {
  let acceptedImageSize: number;
  if (purpose === 'dp') {
    acceptedImageSize = 5 * 1024 * 1024; // 5MB max size
  } else acceptedImageSize = 1 * 1024 * 1024; // 1MB max size
  return z
    .any()
    .refine(
      (file) =>
        file instanceof File && ['image/jpeg', 'image/png'].includes(file.type),
      'Only JPEG or PNG images are allowed'
    )
    .refine(
      (file) => file.size <= acceptedImageSize,
      'Image must be smaller than 5MB'
    );
}

export function hexColorvalidation() {
  const hexColorPattern = /^#([0-9A-F]{3}){1,2}$/i;
  return z
    .string()
    .regex(
      hexColorPattern,
      'Please enter a valid hex color (e.g. #FFF or #FFFFFF)'
    );
}

export const phoneRegex = new RegExp(
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/
);

export function generateRandomString(length = 8) {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export const generateVCard = (client: any) => {
  const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${client.name}
TEL;TYPE=CELL:${client.phone}
EMAIL:${client.email}
END:VCARD`;

  // Create a Blob from the vCard data
  const blob = new Blob([vCardData], { type: 'text/vcard' });
  const url = URL.createObjectURL(blob);

  // Create a temporary anchor element to trigger the download
  const a = document.createElement('a');
  a.href = url;
  a.download = `${client.name}.vcf`;
  a.click();

  // Revoke the object URL after the download
  URL.revokeObjectURL(url);
};

export async function convertImageToBase64(imageFile: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    // Set up the callback for when the file has been read
    reader.onload = () => {
      if (reader.result) {
        const base64String = reader.result as string;
        resolve(base64String);
      } else {
        reject('File reading failed');
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    // Start reading the image file as a data URL
    reader.readAsDataURL(imageFile);
  });
}


export function ensureHttps(url:string) {
  if (typeof url === 'string' && url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url; // Return the original URL if it's already https or not a valid string
}